<script setup lang="ts">
import { useSlots, computed, watch, ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { usePullToRefresh } from '@/composables/pull-to-refresh'
import GlobalBanner from '@/components/shared/GlobalBanner.vue'
import PullToRefreshIndicator from '@/components/shared/PullToRefreshIndicator.vue'

const props = defineProps<{
  background?: string
  footerBackground?: string
  navBarFooter?: boolean
}>()

const route = useRoute()

const slots = useSlots()
const hasSlot = (name: keyof typeof slots) => {
  return !!slots[name]
}

const pageWrapperStyle = computed(() => ({
  '--layout-root-page-bg': props.background
    ? `var(${props.background})`
    : 'transparent'
}))

const footerStyle = computed(() =>
  props.footerBackground
    ? { backgroundColor: `var(${props.footerBackground})` }
    : undefined
)

const bottomInset = computed(() =>
  hasSlot('page-footer') || route.meta.reserveBottomBarNavSpace
    ? 'calc(var(--footerheight) + env(safe-area-inset-bottom, 0px))'
    : 'env(safe-area-inset-bottom, 0px)'
)

watch(
  bottomInset,
  (newInset) => {
    document.documentElement.style.setProperty(
      '--runtime-app-bottom-inset',
      newInset
    )
  },
  { immediate: true }
)

const scrollArea = ref<HTMLDivElement>()
let scrollbarWidthObserver: ResizeObserver | undefined

const updateScrollbarWidth = () => {
  const el = scrollArea.value
  if (!el) {
    return
  }
  document.documentElement.style.setProperty(
    '--runtime-scrollbar-width',
    `${el.offsetWidth - el.clientWidth}px`
  )
}

onMounted(() => {
  updateScrollbarWidth()
  scrollbarWidthObserver = new ResizeObserver(updateScrollbarWidth)
  if (scrollArea.value) {
    scrollbarWidthObserver.observe(scrollArea.value)
  }
})

onUnmounted(() => {
  scrollbarWidthObserver?.disconnect()
})

const { isRefreshing, isDragging, pullDistance, progress } = route.meta
  .pullToRefresh
  ? usePullToRefresh(scrollArea)
  : {
      isRefreshing: ref(false),
      isDragging: ref(false),
      pullDistance: ref(0),
      progress: ref(0)
    }
</script>

<template>
  <div class="page-wrapper" :style="pageWrapperStyle">
    <div class="status-bar-backdrop" aria-hidden="true"></div>
    <PullToRefreshIndicator
      v-if="route.meta.pullToRefresh"
      :is-refreshing="isRefreshing"
      :is-dragging="isDragging"
      :pull-distance="pullDistance"
      :progress="progress"
    />
    <div ref="scrollArea" class="scroll-area">
      <div
        class="scroll-content"
        :class="{ 'scroll-content--dragging': isDragging }"
        :style="{ transform: `translateY(${pullDistance}px)` }"
      >
        <header class="header--bg">
          <slot name="page-heading"></slot>
          <GlobalBanner />
        </header>
        <main>
          <slot name="page-content"></slot>
        </main>
      </div>
      <footer
        v-if="hasSlot('page-footer')"
        :class="{ 'footer--nav-bar': navBarFooter }"
        :style="footerStyle"
      >
        <div class="footer-content">
          <slot name="page-footer"></slot>
        </div>
      </footer>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$global-padding-top: 50px;

main,
header,
.footer-content {
  display: flex;
  max-width: var(--large-screen-width);
  margin: 0 auto;
  width: var(--width-100);
  flex-direction: column;
}

header {
  margin-bottom: 0;
  padding-top: calc(2rem + env(safe-area-inset-top, 0px));

  @media (min-width: 600px) {
    padding-top: calc($global-padding-top + env(safe-area-inset-top, 0px));
  }
}

.header--bg {
  background-color: var(--layout-root-page-bg);
}

main {
  padding-bottom: calc(var(--runtime-app-bottom-inset) + 16px);
  flex: 1;
}

footer {
  @include shell-footer-base;
}

.footer--nav-bar {
  background-color: var(--purple50);
  border-top: 2px solid var(--white);
}

.page-wrapper {
  height: 100vh;
  height: 100dvh;
  position: relative;
}

// Sits above sticky content (z-index 1) so nothing scrolls visibly under the
// status bar, but below modal overlays (z-index 2, later in the DOM).
.status-bar-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: calc(var(--width-100) - var(--runtime-scrollbar-width, 0px));
  height: env(safe-area-inset-top, 0px);
  background-color: var(--layout-root-page-bg);
  z-index: 2;
}

.scroll-area {
  height: 100%;
  overflow-y: auto;
  overscroll-behavior: none;
  display: flex;
  flex-direction: column;
}

.scroll-content {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  transition: transform 200ms ease;
}

.scroll-content--dragging {
  transition: none;
}

$global-padding-sides: 16px;

.scroll-content > header > :deep(*),
.scroll-content > main > :deep(*) {
  padding-left: $global-padding-sides;
  padding-right: $global-padding-sides;
}
.page-wrapper :deep(.layout-width-full) {
  padding-left: 0;
  padding-right: 0;
}
</style>
